import { UseQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { CasinoReport, PaginatedData, Transaction } from '../@types';
import { API_ROUTES } from '../constants/apiRoutes';
import { QUERY_KEYS } from '../constants';
import { USER_ROLES, UserRole } from '../constants/roles';
import useCasinoAccessToken from '../hooks/useCasinoAccessToken';
import useProductAvailability from '../hooks/useProductAvailability';
import { usePaginatedPostQuery } from '../hooks/usePaginatedPostQuery';
import useInfinitePostQuery from '../hooks/useInfinitePostQuery';
import { useRoles } from '../hooks/useRoles';
import {
  CasinoFavoriteCategory,
  CasinoGame,
  CasinoGameUrlResponse,
  CasinoGamesResponse,
  CasinoProvider,
  CasinoProvidersResponse,
} from '../types/casino';
import { ensureCasinoAccessToken, getData, postData } from '../utils/api';

const USER_TO_REQUEST_ROLE_MAP: Partial<Record<UserRole, UserRole>> = {
  [USER_ROLES.SUPER_ADMIN]: USER_ROLES.MANAGER,
  [USER_ROLES.ADMIN]: USER_ROLES.MANAGER,
  [USER_ROLES.MANAGER]: USER_ROLES.AGENT,
  [USER_ROLES.AGENT]: USER_ROLES.PLAYER,
  [USER_ROLES.PLAYER]: USER_ROLES.PLAYER,
};

const CASINO_GAMES_ENDPOINT = 'games?customImgURL=desc&_id=desc';

type CasinoProvidersRequest = {
  limit: number;
  offset: number;
  name: string;
};

type CasinoGamesRequest = {
  providerName: string[];
  categoryId: string;
  offset: number;
  limit: number;
  sortGames: boolean;
  search?: string;
};

type UseCasinoGamesParams = {
  searchTerm?: string;
  providerNames?: string[];
  categoryId?: string;
  limit?: number;
  enabled?: boolean;
};

type CasinoTransactionsHistoryParams = {
  dateFrom?: number;
  dateTo?: number;
  page: number;
  limit: number;
};

type CasinoTransactionsHistoryResponse = {
  transactions: Transaction[];
  totalCount: number;
};

export const useCasinoTransactionsHistory = (
  { dateFrom, dateTo, page, limit }: CasinoTransactionsHistoryParams,
  options?: Pick<UseQueryOptions<CasinoTransactionsHistoryResponse>, 'placeholderData'>
) => {
  const casinoAccessToken = useCasinoAccessToken();

  return useQuery({
    queryKey: [QUERY_KEYS.casinoTransactionsHistory, dateFrom, dateTo, page, limit],
    queryFn: (): Promise<CasinoTransactionsHistoryResponse> =>
      postData(
        API_ROUTES.CASINO_TRANSACTIONS_HISTORY,
        {
          offset: (page - 1) * limit,
          limit,
          dateFrom,
          dateTo,
        },
        'casino'
      ),
    enabled: Boolean(casinoAccessToken),
    ...options,
  });
};

export const useGetCasinoReports = (
  bodyParams: { dateFrom?: number; dateTo?: number } = {},
  options?: Omit<UseQueryOptions<PaginatedData<CasinoReport>>, 'queryKey' | 'queryFn'>
) => {
  const { role } = useRoles();
  const mappedRole = USER_TO_REQUEST_ROLE_MAP[role];

  const requestBody = useMemo(() => {
    return {
      ...bodyParams,
      role: mappedRole?.toLowerCase(),
    };
  }, [bodyParams, mappedRole]);

  return usePaginatedPostQuery<CasinoReport>('transaction/summary', requestBody, 'casino', {
    ...options,
    enabled: (options?.enabled ?? true) && Boolean(mappedRole),
  });
};

export const useInfiniteCasinoReports = (
  bodyParams: { dateFrom?: number; dateTo?: number; limit?: number; [key: string]: unknown } = {},
  options?: Pick<UseQueryOptions<PaginatedData<CasinoReport>>, 'enabled'> & { limit?: number }
) => {
  const { role } = useRoles();
  const mappedRole = USER_TO_REQUEST_ROLE_MAP[role];

  const requestBody = useMemo(() => {
    return {
      ...bodyParams,
      limit: options?.limit ?? 5,
      role: mappedRole?.toLowerCase(),
    };
  }, [bodyParams, mappedRole, options?.limit]);

  return useInfinitePostQuery<CasinoReport>({
    queryKey: [QUERY_KEYS.casinoReports, 'infinite'],
    endpoint: 'transaction/summary',
    bodyParams: requestBody,
    apiService: 'casino',
    enabled: (options?.enabled ?? true) && Boolean(mappedRole),
  });
};

export const useCasinoCategories = (options?: Pick<UseQueryOptions<CasinoFavoriteCategory[]>, 'enabled'>) => {
  const { i18n } = useTranslation();
  const { canViewCasinoContent } = useProductAvailability();

  return useQuery({
    queryKey: [QUERY_KEYS.casinoCategories, i18n.language],
    queryFn: (): Promise<CasinoFavoriteCategory[]> => getData('category/categories/favourite', undefined, 'casino'),
    enabled: canViewCasinoContent && (options?.enabled ?? true),
    select: (data) => (!canViewCasinoContent ? [] : data),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCasinoProviders = () => {
  const { i18n } = useTranslation();
  const { canViewCasinoContent } = useProductAvailability();

  return useQuery({
    queryKey: [QUERY_KEYS.casinoProviders, i18n.language],
    queryFn: (): Promise<CasinoProvidersResponse> =>
      postData('provider/providers', { limit: 1000, offset: 0, name: '' } satisfies CasinoProvidersRequest, 'casino'),
    enabled: canViewCasinoContent,
    select: (data: CasinoProvidersResponse) => (!canViewCasinoContent ? [] : (data.providers ?? [])),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCasinoGames = ({
  searchTerm = '',
  providerNames = [],
  categoryId = '',
  limit = 24,
  enabled = true,
}: UseCasinoGamesParams) => {
  const { i18n } = useTranslation();
  const { canViewCasinoContent } = useProductAvailability();
  const normalizedSearchTerm = searchTerm.trim();

  const query = useInfiniteQuery<CasinoGamesResponse>({
    queryKey: [
      QUERY_KEYS.casinoGames,
      normalizedSearchTerm || null,
      providerNames,
      categoryId || null,
      limit,
      i18n.language,
    ],
    queryFn: ({ pageParam = 0 }) =>
      postData(
        CASINO_GAMES_ENDPOINT,
        {
          providerName: providerNames,
          categoryId,
          offset: pageParam as number,
          limit,
          sortGames: true,
          search: normalizedSearchTerm || undefined,
        } satisfies CasinoGamesRequest,
        'casino'
      ),
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce((total, page) => total + (page.games?.length ?? 0), 0);

      return loadedCount < lastPage.totalCount ? loadedCount : undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    enabled: enabled && canViewCasinoContent,
  });

  const games = useMemo(
    () => (!canViewCasinoContent ? [] : (query.data?.pages.flatMap((page) => page.games ?? []) ?? [])),
    [canViewCasinoContent, query.data?.pages]
  );

  return {
    ...query,
    games,
    data: !canViewCasinoContent ? undefined : query.data,
    totalCount: !canViewCasinoContent ? 0 : (query.data?.pages[0]?.totalCount ?? 0),
  };
};

export const fetchCasinoGameUrl = async (gameId: string): Promise<CasinoGameUrlResponse> => {
  await ensureCasinoAccessToken();
  return getData(`game-url/${gameId}/real`, undefined, 'casino');
};

export const useCasinoGameUrl = (
  gameId: string | undefined,
  options?: Pick<UseQueryOptions<CasinoGameUrlResponse>, 'enabled'>
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEYS.casinoGameUrl, gameId],
    queryFn: () => fetchCasinoGameUrl(gameId as string),
    enabled: Boolean(options?.enabled ?? true) && !!gameId && isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export type { CasinoFavoriteCategory, CasinoGame, CasinoProvider };
